import axios from 'axios';
import type { Cell } from '../game/types';

// Get API base URL from environment or use proxy/default
const getApiUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3002';
  }
  const url = process.env.REACT_APP_API_URL || 'https://api.migoyugo.com';
  return url;
};

const API_URL = process.env.NODE_ENV === 'development' ? '' : getApiUrl();

// Helper function to get admin token from localStorage
const getAdminToken = (): string | null => {
  return localStorage.getItem('adminToken');
};

// Helper function to set admin token in localStorage
const setAdminToken = (token: string): void => {
  localStorage.setItem('adminToken', token);
};

// Helper function to remove admin token from localStorage
const removeAdminToken = (): void => {
  localStorage.removeItem('adminToken');
};

// Create axios instance for admin API
const adminApiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include admin token
adminApiClient.interceptors.request.use(
  (config) => {
    const token = getAdminToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('[AdminApiService] Sending request with admin token:', config.url);
    } else {
      console.warn('[AdminApiService] No admin token found for request:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
adminApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Token expired or invalid - clear it
      removeAdminToken();
    }
    return Promise.reject(error);
  }
);

export interface AdminUser {
  id: number;
  email: string;
  username: string;
  wins: number;
  losses: number;
  draws: number;
  current_streak: number;
  streak_type: 'win' | 'loss' | null;
  created_at: string;
  email_verified: boolean | number;
  is_online: boolean | number;
  in_game: boolean | number;
  is_admin: boolean | number;
  searchable?: boolean | number;
  country_code?: string | null;
  country_name?: string | null;
  last_seen?: string | null;
  elo_rating?: number;
  elo_fast?: number;
  elo_standard?: number;
}

export interface CountryStatistics {
  countryCode: string;
  countryName: string;
  memberCount: number;
  guestCount: number;
  totalGuestGames: number;
}

export interface SystemStats {
  totalUsers: number;
  totalGames: number;
  onlineUsers: number;
  activeGames: number;
}

export interface Milestone {
  value: number;
  formatted: string;
  reachedAt: string;
  notified: boolean;
}

export interface GameRecord {
  id: string;
  user_id: number;
  user_username?: string;
  opponent: string;
  opponent_username?: string;
  result: 'win' | 'loss' | 'draw';
  player_color: 'white' | 'black';
  game_mode: string;
  game_id?: string;
  timestamp: string;
  win_type?: string | null;
  game_end_reason?: string | null;
}

export interface GameFilters {
  dateFrom?: string;
  dateTo?: string;
  gameMode?: string;
  result?: 'win' | 'loss' | 'draw';
  playerSearch?: string;
  limit?: number;
}

export interface IgoPuzzleVerifyResult {
  ok: true;
  attacker: 'white' | 'black';
  maxK: number;
  /** Fewest attacker moves that force an Igo, or null if none within maxK. */
  minimalK: number | null;
  forcedWithin1: boolean;
  forcedWithin2: boolean;
  forcedWithin3: boolean;
  forcedWithin4: boolean;
  forcedWithin5: boolean;
  /** True when there is exactly one forcing plan, counting a dual-key finish as one. */
  unique: boolean;
  uniqueReason: string | null;
  /** Rival first moves that also win, when the answer is not unique. */
  ambiguousMoves: Array<{ row: number; col: number }>;
  winningRootMoves: Array<{ row: number; col: number; immediateIgo: boolean }>;
  /** Distinct winning first-move plans (dual-key finish counts as one). */
  solutionCount: number;
  /** True when more than two distinct forcing plans exist (lines are omitted). */
  moreThanTwoSolutions?: boolean;
  /**
   * Solution lines for display: both when solutionCount is 1–2,
   * empty when moreThanTwoSolutions is true.
   */
  solutions: Array<{
    firstMove: { row: number; col: number };
    line: Array<{ row: number; col: number; player: 'white' | 'black' }>;
    finalMoves: Array<{ row: number; col: number }>;
  }>;
  /** Attacker's plan against the toughest defence, ending in the Igo. */
  solutionLine: Array<{ row: number; col: number; player: 'white' | 'black' }>;
  /** Every square that completes the Igo on the last move. */
  acceptedFinalMoves: Array<{ row: number; col: number }>;
  trivial: boolean;
  publishable: boolean;
  verdict: string;
  suggestedDifficulty: 'easy' | 'medium' | 'hard' | 'expert' | 'insane' | null;
  metrics: {
    pieceCount: number;
    legalMoveCount: number;
    decoyCount: number;
    attackerYugos: number;
    defenderYugos: number;
  };
  /** Hash of the rules module the proof was made against. */
  rulesFingerprint: string;
  totalNodesVisited: number;
  elapsedMs: number;
  timeLimitMs: number;
}

export interface ConstructedPuzzleCandidate {
  position: string;
  board: (Cell | null)[][];
  toMove: 'white' | 'black';
  minimalK: number;
  solutionLine: Array<{ row: number; col: number; player: 'white' | 'black' }>;
  solutionText: string;
  acceptedFinalMoves: Array<{ row: number; col: number }>;
  suggestedDifficulty: 'easy' | 'medium' | 'hard' | 'expert';
  hardness: {
    score: number;
    quietKey: boolean;
    span: { rowSpan: number; colSpan: number };
    suggestedDifficulty: string;
  };
  metrics: {
    pieceCount: number;
    legalMoveCount: number;
    decoyCount: number;
  };
  source?: {
    gameId?: string;
    keptPly?: number;
    mutationsApplied?: number;
  };
}

export interface PuzzleConstructResult {
  ok: true;
  generatedAt: string;
  rulesFingerprint: string;
  settings: Record<string, number>;
  stats: {
    games: number;
    gamesReviewed: number;
    seedsFound: number;
    constructed: number;
    elapsedMs: number;
    rejectedByHardness?: number;
    rejectedLowScore?: number;
  };
  candidates: ConstructedPuzzleCandidate[];
}

export interface PuzzleConstructProgress {
  gamesReviewed: number;
  gamesTotal: number;
  constructed: number;
  seedsFound: number;
  message: string;
}

export interface Tournament {
  id: number;
  name: string;
  status: 'scheduled' | 'registration' | 'in_progress' | 'complete' | 'cancelled';
  created_by: number;
  created_at: string;
  scheduled_start_at?: string | null;
  bracket_size: number;
  player_count: number;
  current_round: number;
  total_rounds: number;
  registration_open: number | boolean;
  is_rated?: number | boolean;
  auto_start?: number | boolean;
  last_auto_start_error?: string | null;
  format?: 'single_elim' | 'swiss';
}

export interface TournamentParticipant {
  id: number;
  tournament_id: number;
  user_id: number;
  username: string;
  seed: number;
  status: string;
  elo_rating: number;
  elo_fast?: number;
  elo_standard?: number;
  wins: number;
  losses: number;
  registered_at: string;
}

export interface TournamentMatch {
  id: number;
  tournament_id: number;
  round: number;
  match_number: number;
  player1_id: number | null;
  player2_id: number | null;
  winner_id: number | null;
  status: string;
  game_id: string | null;
  player1_ready: number;
  player2_ready: number;
  ready_check_started_at: string | null;
  game_started_at: string | null;
  completed_at: string | null;
  player1_username?: string;
  player2_username?: string;
  winner_username?: string;
}

export interface RoundSetting {
  id: number;
  tournament_id: number;
  round: number;
  minutes_per_player: number;
  increment_seconds: number;
}

export class AdminApiService {
  // Admin authentication
  static async login(email: string, password: string) {
    try {
      const response = await adminApiClient.post('/api/admin/login', {
        email,
        password,
      });
      const token = response.data.token;
      if (token) {
        setAdminToken(token);
        console.log('[AdminApiService] Admin token saved:', token.substring(0, 20) + '...');
        // Verify it was saved
        const saved = getAdminToken();
        if (!saved) {
          console.error('[AdminApiService] Token was not saved properly!');
        } else {
          console.log('[AdminApiService] Token verified in storage');
        }
      } else {
        console.error('[AdminApiService] No token in response:', response.data);
      }
      return {
        success: true,
        error: null,
        token,
        user: response.data.user,
      };
    } catch (error: any) {
      console.error('[AdminApiService] Login error:', error.response?.data || error.message);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Admin login failed',
      };
    }
  }

  static async logout() {
    removeAdminToken();
    return { success: true, error: null };
  }

  /** Admin-only: forced Igo within K attacker turns (server uses worker + job poll; avoids gateway timeouts). */
  static async verifyIgoPuzzle(payload: {
    board: (Cell | null)[][];
    toMove: 'white' | 'black';
    /** Per-server clamp: 5s–15m; default 3m on server */
    timeLimitMs?: number;
    /** 1–4, default 4 */
    maxK?: number;
  }): Promise<{ data: IgoPuzzleVerifyResult | null; error: string | null }> {
    const pollIntervalMs = 1200;
    const clientMaxWaitMs = 20 * 60 * 1000;

    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    try {
      const postRes = await adminApiClient.post('/api/admin/puzzle-verify-igo', payload, {
        timeout: 60000,
      });
      const pd = postRes.data;

      // Legacy: synchronous 200 with full body
      if (postRes.status === 200 && pd && typeof pd.forcedWithin2 === 'boolean') {
        return { data: pd as IgoPuzzleVerifyResult, error: null };
      }

      const jobId = pd?.jobId as string | undefined;
      if (postRes.status !== 202 || !jobId) {
        return {
          data: null,
          error: pd?.error || 'Unexpected response from puzzle verify',
        };
      }

      const deadline = Date.now() + clientMaxWaitMs;
      while (Date.now() < deadline) {
        const poll = await adminApiClient.get(`/api/admin/puzzle-verify-igo/job/${jobId}`, {
          timeout: 60000,
          validateStatus: (s) => s < 500,
        });
        if (poll.status === 404) {
          return { data: null, error: 'Verification job expired or not found.' };
        }
        const d = poll.data as {
          status: string;
          error?: string;
          elapsedMs?: number;
          timeLimitMs?: number;
          forcedWithin2?: boolean;
        };

        if (d.status === 'complete') {
          const raw = { ...(poll.data as Record<string, unknown>) };
          delete raw.status;
          delete raw.jobId;
          return { data: raw as unknown as IgoPuzzleVerifyResult, error: null };
        }
        if (d.status === 'error') {
          return {
            data: null,
            error: d.error || 'Puzzle verification failed',
          };
        }
        if (d.status === 'not_found') {
          return { data: null, error: 'Verification job expired or not found.' };
        }
        await sleep(pollIntervalMs);
      }

      return {
        data: null,
        error: 'Timed out waiting for puzzle analysis. Try a shorter time limit or simpler position.',
      };
    } catch (error: any) {
      return {
        data: null,
        error: error.response?.data?.error || error.message || 'Puzzle verification failed',
      };
    }
  }

  /**
   * Admin-only: build hard forced-Igo puzzles from the game archive.
   * Uses a worker job + poll, same pattern as verifyIgoPuzzle.
   */
  static async constructHardPuzzles(
    payload: {
      games?: number;
      keep?: number;
      minScore?: number;
      minK?: number;
      maxK?: number;
      mutations?: number;
      timeLimitMs?: number;
    } = {},
    onProgress?: (progress: PuzzleConstructProgress) => void
  ): Promise<{ data: PuzzleConstructResult | null; error: string | null }> {
    const pollIntervalMs = 2000;
    const clientMaxWaitMs = 90 * 60 * 1000;
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    try {
      const postRes = await adminApiClient.post('/api/admin/puzzle-construct', payload, {
        timeout: 60000,
      });
      const pd = postRes.data;
      const jobId = pd?.jobId as string | undefined;
      if (postRes.status !== 202 || !jobId) {
        return {
          data: null,
          error: pd?.error || 'Unexpected response from puzzle construct',
        };
      }

      const deadline = Date.now() + clientMaxWaitMs;
      while (Date.now() < deadline) {
        const poll = await adminApiClient.get(`/api/admin/puzzle-construct/job/${jobId}`, {
          timeout: 60000,
          validateStatus: (s) => s < 500,
        });
        if (poll.status === 404) {
          return { data: null, error: 'Construct job expired or not found.' };
        }
        const d = poll.data as {
          status: string;
          error?: string;
          progress?: PuzzleConstructProgress;
          ok?: boolean;
          candidates?: ConstructedPuzzleCandidate[];
        };

        if (d.progress && onProgress) {
          onProgress(d.progress);
        }

        if (d.status === 'complete') {
          const raw = { ...(poll.data as Record<string, unknown>) };
          delete raw.status;
          delete raw.jobId;
          return { data: raw as unknown as PuzzleConstructResult, error: null };
        }
        if (d.status === 'error') {
          return { data: null, error: d.error || 'Puzzle construction failed' };
        }
        if (d.status === 'not_found') {
          return { data: null, error: 'Construct job expired or not found.' };
        }
        await sleep(pollIntervalMs);
      }

      return {
        data: null,
        error: 'Timed out waiting for puzzle construction. Try fewer games or a lower min-score.',
      };
    } catch (error: any) {
      return {
        data: null,
        error: error.response?.data?.error || error.message || 'Puzzle construction failed',
      };
    }
  }

  // User management
  static async getAllUsers(): Promise<{ users: AdminUser[]; error: string | null }> {
    try {
      const response = await adminApiClient.get('/api/admin/users');
      return { users: response.data.users || [], error: null };
    } catch (error: any) {
      return {
        users: [],
        error: error.response?.data?.error || error.message || 'Failed to get users',
      };
    }
  }

  static async getUserById(userId: number): Promise<{ user: AdminUser | null; error: string | null }> {
    try {
      const response = await adminApiClient.get(`/api/admin/users/${userId}`);
      return { user: response.data.user, error: null };
    } catch (error: any) {
      return {
        user: null,
        error: error.response?.data?.error || error.message || 'Failed to get user',
      };
    }
  }

  static async updateUser(
    userId: number,
    updates: { username?: string; email?: string; is_admin?: boolean; searchable?: boolean }
  ): Promise<{ user: AdminUser | null; error: string | null }> {
    try {
      const response = await adminApiClient.put(`/api/admin/users/${userId}`, updates);
      return { user: response.data.user, error: null };
    } catch (error: any) {
      return {
        user: null,
        error: error.response?.data?.error || error.message || 'Failed to update user',
      };
    }
  }

  static async deleteUser(userId: number): Promise<{ success: boolean; error: string | null }> {
    try {
      await adminApiClient.delete(`/api/admin/users/${userId}`);
      return { success: true, error: null };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to delete user',
      };
    }
  }

  // System statistics
  static async getSystemStats(): Promise<{ stats: SystemStats | null; error: string | null }> {
    try {
      const response = await adminApiClient.get('/api/admin/stats');
      return { stats: response.data.stats, error: null };
    } catch (error: any) {
      return {
        stats: null,
        error: error.response?.data?.error || error.message || 'Failed to get system stats',
      };
    }
  }

  static async getRecentUsers(limit: number = 10): Promise<{ users: AdminUser[]; error: string | null }> {
    try {
      const response = await adminApiClient.get('/api/admin/recent-users', {
        params: { limit },
      });
      return { users: response.data.users || [], error: null };
    } catch (error: any) {
      return {
        users: [],
        error: error.response?.data?.error || error.message || 'Failed to get recent users',
      };
    }
  }

  static async getTopPlayers(limit: number = 10): Promise<{ players: AdminUser[]; error: string | null }> {
    try {
      const response = await adminApiClient.get('/api/admin/top-players', {
        params: { limit },
      });
      return { players: response.data.players || [], error: null };
    } catch (error: any) {
      return {
        players: [],
        error: error.response?.data?.error || error.message || 'Failed to get top players',
      };
    }
  }

  // Game history
  static async getUserGames(userId: number, limit: number = 50): Promise<{ games: GameRecord[]; error: string | null }> {
    try {
      const response = await adminApiClient.get(`/api/admin/users/${userId}/games`, {
        params: { limit },
      });
      return { games: response.data.games || [], error: null };
    } catch (error: any) {
      return {
        games: [],
        error: error.response?.data?.error || error.message || 'Failed to get user games',
      };
    }
  }

  // Get all games with filtering
  static async getAllGames(filters: GameFilters = {}): Promise<{ games: GameRecord[]; error: string | null }> {
    try {
      const response = await adminApiClient.get('/api/admin/games', {
        params: filters,
      });
      return { games: response.data.games || [], error: null };
    } catch (error: any) {
      return {
        games: [],
        error: error.response?.data?.error || error.message || 'Failed to get games',
      };
    }
  }

  // Analytics
  static async getUserGrowthAnalytics(period: 'daily' | 'weekly' | 'monthly' = 'daily'): Promise<{ analytics: any[]; error: string | null }> {
    try {
      const response = await adminApiClient.get('/api/admin/analytics/user-growth', {
        params: { period },
      });
      return { analytics: response.data.analytics || [], error: null };
    } catch (error: any) {
      return {
        analytics: [],
        error: error.response?.data?.error || error.message || 'Failed to get user growth analytics',
      };
    }
  }

  static async getGameActivityAnalytics(period: 'daily' | 'weekly' | 'monthly' = 'daily'): Promise<{ analytics: any[]; error: string | null }> {
    try {
      const response = await adminApiClient.get('/api/admin/analytics/game-activity', {
        params: { period },
      });
      return { analytics: response.data.analytics || [], error: null };
    } catch (error: any) {
      return {
        analytics: [],
        error: error.response?.data?.error || error.message || 'Failed to get game activity analytics',
      };
    }
  }

  static async getVisitorAnalytics(period: 'daily' | 'weekly' | 'monthly' = 'daily'): Promise<{ analytics: any | null; error: string | null }> {
    try {
      const response = await adminApiClient.get('/api/admin/analytics/visitors', {
        params: { period },
      });
      return { analytics: response.data.analytics || null, error: null };
    } catch (error: any) {
      return {
        analytics: null,
        error: error.response?.data?.error || error.message || 'Failed to get visitor analytics',
      };
    }
  }

  static async getGuestActivityAnalytics(period: 'daily' | 'weekly' | 'monthly' = 'daily'): Promise<{ analytics: any[]; error: string | null }> {
    try {
      const response = await adminApiClient.get('/api/admin/analytics/guest-activity', {
        params: { period },
      });
      return { analytics: response.data.analytics || [], error: null };
    } catch (error: any) {
      return {
        analytics: [],
        error: error.response?.data?.error || error.message || 'Failed to get guest activity analytics',
      };
    }
  }

  static async getWinRatesAnalytics(): Promise<{ analytics: any[]; error: string | null }> {
    try {
      const response = await adminApiClient.get('/api/admin/analytics/win-rates');
      return { analytics: response.data.analytics || [], error: null };
    } catch (error: any) {
      return {
        analytics: [],
        error: error.response?.data?.error || error.message || 'Failed to get win rates analytics',
      };
    }
  }

  static async getPopularGameModes(limit: number = 10): Promise<{ analytics: any[]; error: string | null }> {
    try {
      const response = await adminApiClient.get('/api/admin/analytics/popular-modes', {
        params: { limit },
      });
      return { analytics: response.data.analytics || [], error: null };
    } catch (error: any) {
      return {
        analytics: [],
        error: error.response?.data?.error || error.message || 'Failed to get popular game modes',
      };
    }
  }

  static async getPeakUsageTimes(): Promise<{ analytics: any[]; error: string | null }> {
    try {
      const response = await adminApiClient.get('/api/admin/analytics/peak-usage');
      return { analytics: response.data.analytics || [], error: null };
    } catch (error: any) {
      return {
        analytics: [],
        error: error.response?.data?.error || error.message || 'Failed to get peak usage times',
      };
    }
  }

  static async getCountryStatistics(): Promise<{ statistics: CountryStatistics[]; error: string | null }> {
    try {
      const response = await adminApiClient.get('/api/admin/analytics/countries');
      return { statistics: response.data.statistics || [], error: null };
    } catch (error: any) {
      return {
        statistics: [],
        error: error.response?.data?.error || error.message || 'Failed to get country statistics',
      };
    }
  }

  // Milestones
  static async getMilestones(): Promise<{ milestones: Milestone[]; error: string | null }> {
    try {
      const response = await adminApiClient.get('/api/admin/milestones');
      return { milestones: response.data.milestones || [], error: null };
    } catch (error: any) {
      return {
        milestones: [],
        error: error.response?.data?.error || error.message || 'Failed to get milestones',
      };
    }
  }

  // ─── Puzzle submissions review ─────────────────────────────────────

  static async getPendingSubmissionCount(): Promise<{ count: number; error: string | null }> {
    try {
      const response = await adminApiClient.get('/api/admin/puzzle-submissions/count');
      return { count: response.data.count ?? 0, error: null };
    } catch (error: any) {
      return { count: 0, error: error.response?.data?.error || error.message || 'Failed to get count' };
    }
  }

  static async getPendingSubmissions(): Promise<{ puzzles: any[]; error: string | null }> {
    try {
      const response = await adminApiClient.get('/api/admin/puzzle-submissions');
      return { puzzles: response.data.puzzles || [], error: null };
    } catch (error: any) {
      return { puzzles: [], error: error.response?.data?.error || error.message || 'Failed to get submissions' };
    }
  }

  static async approvePuzzle(id: number, message?: string): Promise<{ success: boolean; error: string | null }> {
    try {
      await adminApiClient.post(`/api/admin/puzzle-submissions/${id}/approve`, { message });
      return { success: true, error: null };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || error.message || 'Failed to approve puzzle' };
    }
  }

  static async rejectPuzzle(id: number, message?: string): Promise<{ success: boolean; error: string | null }> {
    try {
      await adminApiClient.post(`/api/admin/puzzle-submissions/${id}/reject`, { message });
      return { success: true, error: null };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || error.message || 'Failed to reject puzzle' };
    }
  }

  // Token management
  static isAuthenticated(): boolean {
    return getAdminToken() !== null;
  }

  // ─── Tournament admin methods ────────────────────────────────────────

  static async getTournaments(): Promise<{ tournaments: Tournament[]; error: string | null }> {
    try {
      const response = await adminApiClient.get('/api/admin/tournaments');
      return { tournaments: response.data.tournaments || [], error: null };
    } catch (error: any) {
      return { tournaments: [], error: error.response?.data?.error || error.message || 'Failed to get tournaments' };
    }
  }

  static async createTournament(
    name: string,
    scheduledStartAt?: string,
    isRated?: boolean,
    autoStart?: boolean,
    format?: 'single_elim' | 'swiss'
  ): Promise<{ tournament: Tournament | null; error: string | null }> {
    try {
      const response = await adminApiClient.post('/api/admin/tournaments', {
        name,
        scheduledStartAt,
        isRated: !!isRated,
        autoStart: !!autoStart,
        format: format || 'single_elim'
      });
      return { tournament: response.data.tournament, error: null };
    } catch (error: any) {
      return { tournament: null, error: error.response?.data?.error || error.message || 'Failed to create tournament' };
    }
  }

  static async setTournamentFormat(
    id: number,
    format: 'single_elim' | 'swiss'
  ): Promise<{ tournament: Tournament | null; error: string | null }> {
    try {
      const response = await adminApiClient.post(`/api/admin/tournaments/${id}/format`, { format });
      return { tournament: response.data.tournament, error: null };
    } catch (error: any) {
      return { tournament: null, error: error.response?.data?.error || error.message || 'Failed to update format' };
    }
  }

  static async setSwissRounds(
    id: number,
    rounds: number
  ): Promise<{ tournament: Tournament | null; error: string | null }> {
    try {
      const response = await adminApiClient.post(`/api/admin/tournaments/${id}/swiss-rounds`, { rounds });
      return { tournament: response.data.tournament, error: null };
    } catch (error: any) {
      return { tournament: null, error: error.response?.data?.error || error.message || 'Failed to update Swiss rounds' };
    }
  }

  static async setTournamentRated(id: number, isRated: boolean): Promise<{ tournament: Tournament | null; error: string | null }> {
    try {
      const response = await adminApiClient.post(`/api/admin/tournaments/${id}/rated`, { isRated });
      return { tournament: response.data.tournament, error: null };
    } catch (error: any) {
      return { tournament: null, error: error.response?.data?.error || error.message || 'Failed to update rated setting' };
    }
  }

  static async setTournamentAutoStart(id: number, autoStart: boolean): Promise<{ tournament: Tournament | null; error: string | null }> {
    try {
      const response = await adminApiClient.post(`/api/admin/tournaments/${id}/auto-start`, { autoStart });
      return { tournament: response.data.tournament, error: null };
    } catch (error: any) {
      return { tournament: null, error: error.response?.data?.error || error.message || 'Failed to update auto-start setting' };
    }
  }

  static async deleteTournament(id: number): Promise<{ success: boolean; error: string | null }> {
    try {
      await adminApiClient.delete(`/api/admin/tournaments/${id}`);
      return { success: true, error: null };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || error.message || 'Failed to delete tournament' };
    }
  }

  static async getTournamentDetail(id: number): Promise<{ tournament: Tournament | null; participants: TournamentParticipant[]; matches: TournamentMatch[]; roundSettings: RoundSetting[]; error: string | null }> {
    try {
      const response = await adminApiClient.get(`/api/admin/tournaments/${id}`);
      return {
        tournament: response.data.tournament,
        participants: response.data.participants || [],
        matches: response.data.matches || [],
        roundSettings: response.data.roundSettings || [],
        error: null
      };
    } catch (error: any) {
      return { tournament: null, participants: [], matches: [], roundSettings: [], error: error.response?.data?.error || error.message || 'Failed to get tournament' };
    }
  }

  static async addParticipant(tournamentId: number, userId: number): Promise<{ participants: TournamentParticipant[]; error: string | null }> {
    try {
      const response = await adminApiClient.post(`/api/admin/tournaments/${tournamentId}/participants`, { userId });
      return { participants: response.data.participants || [], error: null };
    } catch (error: any) {
      return { participants: [], error: error.response?.data?.error || error.message || 'Failed to add participant' };
    }
  }

  static async removeParticipant(tournamentId: number, userId: number): Promise<{ participants: TournamentParticipant[]; error: string | null }> {
    try {
      const response = await adminApiClient.delete(`/api/admin/tournaments/${tournamentId}/participants/${userId}`);
      return { participants: response.data.participants || [], error: null };
    } catch (error: any) {
      return { participants: [], error: error.response?.data?.error || error.message || 'Failed to remove participant' };
    }
  }

  static async setParticipantSeeds(
    tournamentId: number,
    seeds: { userId: number; seed: number }[]
  ): Promise<{ participants: TournamentParticipant[]; error: string | null }> {
    try {
      const response = await adminApiClient.post(`/api/admin/tournaments/${tournamentId}/seeds`, { seeds });
      return { participants: response.data.participants || [], error: null };
    } catch (error: any) {
      return { participants: [], error: error.response?.data?.error || error.message || 'Failed to update seeds' };
    }
  }

  static async setRegistrationOpen(tournamentId: number, open: boolean): Promise<{ error: string | null }> {
    try {
      await adminApiClient.post(`/api/admin/tournaments/${tournamentId}/registration`, { open });
      return { error: null };
    } catch (error: any) {
      return { error: error.response?.data?.error || error.message || 'Failed to update registration' };
    }
  }

  static async setRoundTimeControl(tournamentId: number, round: number, minutesPerPlayer: number, incrementSeconds: number): Promise<{ error: string | null }> {
    try {
      await adminApiClient.post(`/api/admin/tournaments/${tournamentId}/round-settings`, { round, minutesPerPlayer, incrementSeconds });
      return { error: null };
    } catch (error: any) {
      return { error: error.response?.data?.error || error.message || 'Failed to set time control' };
    }
  }

  static async startTournament(id: number): Promise<{ error: string | null }> {
    try {
      await adminApiClient.post(`/api/admin/tournaments/${id}/start`);
      return { error: null };
    } catch (error: any) {
      return { error: error.response?.data?.error || error.message || 'Failed to start tournament' };
    }
  }

  static async openRound(tournamentId: number, round: number): Promise<{ error: string | null }> {
    try {
      await adminApiClient.post(`/api/admin/tournaments/${tournamentId}/open-round`, { round });
      return { error: null };
    } catch (error: any) {
      return { error: error.response?.data?.error || error.message || 'Failed to open round' };
    }
  }

  static async forceResolveMatch(
    matchId: number,
    winnerId: number | null,
    outcome?: 'draw' | 'void'
  ): Promise<{ error: string | null }> {
    try {
      await adminApiClient.post(`/api/admin/tournaments/matches/${matchId}/force-resolve`, { winnerId, outcome });
      return { error: null };
    } catch (error: any) {
      return { error: error.response?.data?.error || error.message || 'Failed to force resolve match' };
    }
  }

  static async cancelTournament(id: number): Promise<{ error: string | null }> {
    try {
      await adminApiClient.post(`/api/admin/tournaments/${id}/cancel`);
      return { error: null };
    } catch (error: any) {
      return { error: error.response?.data?.error || error.message || 'Failed to cancel tournament' };
    }
  }

  static getToken(): string | null {
    return getAdminToken();
  }
}

